import React from "react";
// The design team's own tiles, pulled in as components so a className can size
// them. Each one draws its own tinted square and the glyph inside it, so
// nothing here has a fill or a colour of its own to keep in step with them.
import { ReactComponent as ReceivedIcon } from "../../../assets/svg/revenue/rupee.svg";
import { ReactComponent as PendingIcon } from "../../../assets/svg/revenue/Pending payment.svg";
import { ReactComponent as CancelledIcon } from "../../../assets/svg/revenue/Cancelled payment.svg";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import type { RevenueTotals } from "../types/revenue.types";

interface RevenueTileProps {
  /** The tile's glyph, square and all — see the imports above. */
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  label: string;
  amount: number;
  phase: LoadPhase;
  /** The list never arrived, so there is no figure — see below. */
  failed: boolean;
}

/**
 * One figure of the three. The tile carries the currency in its square, so the
 * figure beside it is written as a plain number — a second ₹ an inch away would
 * read as two.
 */
const RevenueTile: React.FC<RevenueTileProps> = ({
  Icon,
  label,
  amount,
  phase,
  failed,
}) => (
  <div className="flex items-center gap-3 px-5 sm:px-6 py-4 3xl:py-5">
    {/* Sized on the wrapper rather than on the glyph, so the artwork keeps its
        own proportions whatever this is set to. */}
    <span
      aria-hidden="true"
      className="h-[42px] w-[42px] 3xl:h-12 3xl:w-12 flex-shrink-0"
    >
      <Icon className="w-full h-full" />
    </span>
    <div className="min-w-0">
      {/* A total is a sum of rows that are not in yet, so while the list is out
          there is no figure to write — and a "0" here would read as a workspace
          that has taken nothing. A list that never arrived is the same: a dash,
          not a zero. The line is held at its own height either way, so nothing
          shifts when the number lands. */}
      {failed ? (
        <div className="flex h-[27px] 3xl:h-[32px] items-center text-[22px] 3xl:text-[26px] font-medium text-ink-muted leading-tight">
          -
        </div>
      ) : phase === "ready" ? (
        <div
          className="text-[22px] 3xl:text-[26px] font-medium text-ink leading-tight truncate"
          // What is written is the number; what is read out is the money, and
          // what the number is of.
          aria-label={`${formatPrice(amount)} ${label.toLowerCase()}`}
        >
          {amount.toLocaleString("en-IN")}
        </div>
      ) : (
        <div className="flex h-[27px] 3xl:h-[32px] items-center">
          <SkeletonCard phase={phase} className="h-5 w-[96px]" />
        </div>
      )}
      <p className="text-[13px] 3xl:text-[14px] text-ink-muted truncate">
        {label}
      </p>
    </div>
  </div>
);

interface RevenueSummaryProps {
  totals: RevenueTotals;
  /**
   * Where the list is. The figures are a sum of it, so all three wait, shimmer
   * and land together — see `RevenueTile`.
   */
  phase?: LoadPhase;
  /** The list could not be loaded, so all three read as a dash rather than 0. */
  failed?: boolean;
}

/**
 * The three figures above the table: what has come in, what is still out, and
 * what was called off — each one measured over the period the screen is set to,
 * so a tile and the rows under it always cover the same stretch of time.
 */
const RevenueSummary: React.FC<RevenueSummaryProps> = ({
  totals,
  phase = "ready",
  failed = false,
}) => (
  <section className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-line rounded-md border border-line bg-white">
    <RevenueTile
      Icon={ReceivedIcon}
      label="Total payment received"
      amount={totals.received}
      phase={phase}
      failed={failed}
    />
    <RevenueTile
      Icon={PendingIcon}
      label="Pending payment"
      amount={totals.pending}
      phase={phase}
      failed={failed}
    />
    <RevenueTile
      Icon={CancelledIcon}
      label="Cancelled payment"
      amount={totals.cancelled}
      phase={phase}
      failed={failed}
    />
  </section>
);

export default RevenueSummary;
